<template>
    <v-responsive>
        <v-row>
            <slot name="title" />
            <v-col>
                <slot name="top-left" />
                <slot name="create-btn">
                    <create-btn
                        v-if="createRoute"
                        :to="createRoute"
                        :resource="resourceName"
                    />
                </slot>
            </v-col>
            <v-col class="text-right">
                <slot name="top-right"></slot>
                <v-btn
                    icon
                    large
                    :loading="downloading"
                    @click="download"
                    v-if="exportable"
                >
                    <v-icon>
                        mdi-download
                    </v-icon>
                </v-btn>
                <ToggleFilterBtn v-if="hasFilters" />
            </v-col>
        </v-row>

        <v-data-table
            :headers="tableHeaders"
            :items="items"
            :server-items-length="totalItems"
            :loading="loading"
            :items-per-page="perPage"
            :footer-props="{
                'items-per-page-options': [5, 10, 20, 30, 40, 50],
                'show-first-last-page': true,
                'show-current-page': true,
            }"
            :class="[cellPaddingClass, elevationClass, 'fill-height']"
            @update:options="optionsUpdated"
            :show-expand="isExpansible"
            single-expand
            @item-expanded="handleItemExpanded"
            :sort-by="sortTable.prop"
            :sort-desc="sortTable.sortDesc"
        >
            <template v-if="draggableTable" v-slot:body="props">
                <Draggable
                    :list="props.items"
                    tag="tbody"
                    handle=".grab-icon"
                    @end="onEnd(props.items)"
                >
                    <tr v-for="item in props.items" :key="item.id">
                        <td
                            v-for="header in tableHeaders"
                            :key="header.value"
                            :class="header.align ? `text-${header.align}` : ''"
                        >
                            <slot :name="header.value" :item="item">
                                {{ item[header.value] }}
                            </slot>
                        </td>
                    </tr>
                </Draggable>
            </template>

            <template
                v-else
                v-for="header in filteredReservedHeaders"
                v-slot:[getSlotName(header)]="{ item }"
            >
                <slot :name="header.value" :item="item">
                    <template v-if="header.value == 'action'">
                        <ResourceActions
                            :model="item"
                            pageContext="index"
                            @click="performAction(item, $event)"
                        />
                    </template>
                    <template v-else>
                        {{ item[header.value] }}
                    </template>
                </slot>
            </template>

            <template
                v-if="isExpansible"
                v-slot:expanded-item="{ headers, item }"
            >
                <td class="pb-4 pt-4" :colspan="headers.length">
                    <template>
                        <slot name="content" :item="item"></slot>
                    </template>
                </td>
            </template>
        </v-data-table>

        <slot name="actionHandler">
            <action-handler ref="actionHandler" @modelChanged="getItems()" />
        </slot>

        <confirm
            ref="downloadConfirm"
            width="600"
            confirmColor="success"
            confirmText="Ok"
            cancelText="Fechar"
        ></confirm>
    </v-responsive>
</template>

<script>
import ActionHandler from './ActionHandler'
import Confirm from './Confirm'
import Draggable from 'vuedraggable'
import ResourceActions from './ResourceActions'
import ToggleFilterBtn from './filters/ToggleFilterBtn'

export default {
    components: {
        ActionHandler,
        Confirm,
        Draggable,
        ResourceActions,
        ToggleFilterBtn,
    },

    props: {
        query: {
            type: Function,
            default: () => {
                return null
            },
        },

        isExpansible: {
            type: Boolean,
            default: () => {
                return false
            },
        },

        exportable: {
            type: Boolean,
            default: () => {
                return false
            },
        },

        model: {
            type: Object,
            required: true,
        },

        createRoute: {
            type: Object,
            default: undefined,
        },

        resourceName: {
            type: String,
            default: undefined,
        },

        sort: {
            type: Object,
            default: () => {
                return {
                    sortDesc: false,
                    prop: 'id',
                }
            },
            validator: value => {
                return 'sortDesc' in value && 'prop' in value
            },
        },

        headers: {
            type: Array,
        },

        topLeftColumns: {
            type: Number,
            default: () => {
                return 9
            },
        },

        searchColumns: {
            type: Number,
            default: () => {
                return 3
            },
        },

        cellPadding: {
            type: Boolean,
            default: () => {
                return false
            },
        },

        elevation: {
            type: Boolean,
            default: () => {
                return true
            },
        },

        draggableTable: {
            type: Boolean,
            default: () => {
                return false
            },
        },

        hiddenItems: {
            type: Boolean,
            default: () => {
                return false
            },
        },

        only: undefined,
    },

    data: () => {
        return {
            loading: true,
            items: [],
            totalItems: null,
            perPage: 20,
            page: 1,
            search: '',
            debounce: null,
            sortTable: {
                sortDesc: false,
                prop: 'id',
            },
            hasFilters: false,
            applyFilters: null,
            downloading: false,
        }
    },

    watch: {
        filters: {
            handler: function(value) {
                this.setFilters(value[this.model._type])
                this.getItems()
            },
        },
    },

    computed: {
        tableHeaders() {
            let headers = this.headers || this.model.tableColumns

            return headers.filter(each => {
                return (
                    each.hide !== true &&
                    (this.only === undefined || this.only.includes(each.value))
                )
            })
        },

        filteredReservedHeaders() {
            return this.tableHeaders.filter(each => {
                return each.value !== 'data-table-expand'
            })
        },

        cellPaddingClass() {
            return this.cellPadding ? 'table-cell-padding' : ''
        },

        elevationClass() {
            return this.elevation ? 'elevation-1' : ''
        },

        filters() {
            return this.$store.getters['filters/current']
        },

        actionHandler() {
            if (this.$scopedSlots.actionHandler) {
                /**
                 * todo
                 * improve this, i don't know how get slot action handler without using refs
                 * ideally should get vue component child of slot, but it's state is undefined at this point
                 */
                return this.$scopedSlots.actionHandler()[0].context.$refs
                    .actionHandler
            }
            return this.$refs.actionHandler
        },
    },

    methods: {
        handleItemExpanded(item) {
            this.$emit('item-expanded', item)
        },

        getSlotName(header) {
            return `item.${header.value}`
        },

        async getItems() {
            this.loading = true
            this.items = []

            try {
                let model = this.model.tableBaseQuery || this.model

                if (this.query) this.query(model)
                if (this.applyFilters) this.applyFilters(model)

                if (this.sortTable.prop) {
                    if (this.sortTable.sortDesc) {
                        model.orderByDesc(this.sortTable.prop)
                    } else {
                        model.orderBy(this.sortTable.prop)
                    }
                }

                let response = await model.paginate(this.perPage, this.page)
                this.items = response.data

                if (!response) return
                this.totalItems = response.meta.pagination.total
                this.setAllowedFilters(response)
            } finally {
                this.loading = false
            }

            this.$emit('itemsFetched', this.items)
        },

        setAllowedFilters(response) {
            if (response.meta && response.meta.filters) {
                this.hasFilters = true
                this.$bus.$emit('filters', {
                    resource: this.model._type,
                    allowed: response.meta.filters.allowed,
                })
            }
        },

        setFilters(filters) {
            if (!filters) return

            this.applyFilters = function(model) {
                let keys = Object.keys(filters)
                keys.forEach(key => {
                    model.where(key, filters[key])
                })
            }
        },

        optionsUpdated(options) {
            this.perPage = options.itemsPerPage
            this.page = options.page

            this.sortTable.sortDesc = options.sortDesc[0]
            this.sortTable.prop = options.sortBy[0]

            if (!this.loading) this.getItems()
        },

        onInputSearch() {
            this.debounce()
            this.$emit('input-search', this.search)
        },

        onEnd(list) {
            this.$emit('reorder', list)
        },

        performAction(item, event) {
            const handler = this.actionHandler[event.type]

            if (handler === undefined) {
                this.$emit(event.type, item)
                return
            }

            handler(item)
        },

        async download() {
            this.downloading = true

            const url = `${this.model.resourceUrl()}export?${this.$store.getters[
                'filters/queryString'
            ](this.model._type)}`

            this.$http
                .get(url)
                .then(response => {
                    if (response) {
                        this.$refs.downloadConfirm.open(
                            'Solicitação de relatório',
                            response.data.message,
                        )
                    }
                })
                .finally(() => {
                    this.downloading = false
                })
        },
    },

    mounted() {
        this.sortTable.sortDesc = this.sort.sortDesc
        this.sortTable.prop = this.sort.prop
        const currentFilters = this.$store.getters[
            'filters/getResourceFilters'
        ](this.model._type)
        this.setFilters(currentFilters)
        this.debounce = this.$lodash.debounce(this.getItems, 400) // Espera 400 ms depois do input
        this.getItems()
    },
}
</script>
